import * as React from "react";
import { Button, Switch } from "@jhool-io/fe-components";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import NoteContent from "../NoteContent/NoteContent";
import Messages from "../Messages/Messages";
import SingleNoteActions from "../SingleNoteActions/SingleNoteActions";
import { UserPermisions } from "../../../../utils/types/user";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import ChangesRequested from "../ChangesRequested/ChangesRequested";
import NoteActivityList from "../NoteActivityList/NoteActivityList";
import { useAppSelector } from "../../../../hooks/useRedux";
import PrescriptionList from "../../../e-prescirbe/components/PrescriptionList/PrescriptionList";
import ClientMedicationList from "../../../e-prescirbe/components/ClientMedicationList/ClientMedicationList";
import ClientAllergyList from "../../../e-prescirbe/components/ClientAllergyList/ClientAllergyList";
import useCanViewEPrescribeFlow from "../../../e-prescirbe/hooks/useCanViewEPrescribeFlow";
import { AiSessionNoteReviewBoundary } from "../../../../context/AiSessionNoteReviewContext";
import { isMedNote } from "../../helpers/notes.helpers";

function NoteItems() {
    const [searchParams, setSearchParams] = useSearchParams();

    const location = useLocation();
    const params = useParams();

    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const requestedNoteTab = searchParams.get("note_tab") || "content";

    const isNoteView = location.pathname.includes("/notes");

    const loggedInUser = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);
    const canViewEPrescribeFlow = useCanViewEPrescribeFlow();

    const handleSetTabInUrl = (noteTab: string) => {
        searchParams.set("note_tab", noteTab);
        setSearchParams(searchParams);
    };

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );
    const canViewNoteEPrescribeFlow =
        canViewEPrescribeFlow && isMedNote(data?.data.type);
    const noteTabInUrl =
        !canViewNoteEPrescribeFlow &&
        ["prescriptions", "allergies", "medications"].includes(requestedNoteTab)
            ? "content"
            : requestedNoteTab;

    const switchOptions = [
        {
            title: "Content",
            key: "content",
            onClick: () => handleSetTabInUrl("content"),
        },
        {
            title: "Messages",
            key: "messages",
            onClick: () => handleSetTabInUrl("messages"),
        },
        {
            title: "Change requested",
            key: "change_requested",
            onClick: () => handleSetTabInUrl("change_requested"),
        },
        {
            title: "Activity",
            key: "activity",
            onClick: () => handleSetTabInUrl("activity"),
        },
        {
            title: "Prescriptions",
            key: "prescriptions",
            onClick: () => handleSetTabInUrl("prescriptions"),
            hidden: !canViewNoteEPrescribeFlow,
        },
        {
            title: "Allergies",
            key: "allergies",
            onClick: () => handleSetTabInUrl("allergies"),
            hidden: !canViewNoteEPrescribeFlow,
        },
        {
            title: "Medications",
            key: "medications",
            onClick: () => handleSetTabInUrl("medications"),
            hidden: !canViewNoteEPrescribeFlow,
        },
    ];

    const canRequestChanges =
        loggedInUser &&
        practice?.is_supervisor &&
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_CHANGE_REQUEST_WRITE
        ) &&
        !data?.data.is_archived;

    return (
        <>
            <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                <div className="max-w-full overflow-x-auto">
                    <Switch
                        switchOptions={switchOptions.filter(
                            (option) => !option.hidden
                        )}
                        activeSwitch={noteTabInUrl}
                    />
                </div>
                {!isNoteView ? (
                    <div className="flex items-center gap-x-[--spacing-items]">
                        {canRequestChanges && (
                            <Button variant="secondary">
                                Request for changes
                            </Button>
                        )}
                        <SingleNoteActions />
                    </div>
                ) : null}
            </div>
            <div className="mt-[--spacing-header]">
                {noteTabInUrl === "content" && <NoteContent />}
                {noteTabInUrl === "messages" && <Messages />}
                {noteTabInUrl === "change_requested" && <ChangesRequested />}
                {noteTabInUrl === "activity" && <NoteActivityList />}
                {noteTabInUrl === "prescriptions" && <PrescriptionList />}
                {noteTabInUrl === "medications" && <ClientMedicationList />}
                {noteTabInUrl === "allergies" && <ClientAllergyList />}
            </div>
        </>
    );
}

export default function NoteItemsWrapper() {
    const { noteId } = useParams();
    return (
        <AiSessionNoteReviewBoundary noteId={noteId as string}>
            <NoteItems />
        </AiSessionNoteReviewBoundary>
    );
}
